import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "cs_chat_conversation_id";

const WELCOME = {
  id: "welcome", sender_type: "ai_agent",
  message: "Hi! I'm the Cornerstone assistant. Ask me about our courses, teachers, fees or how to enrol 🎵",
};

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const convoRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved && !convoRef.current) {
      convoRef.current = saved;
      fetch(`${API}/api/public/ai/conversations/${saved}/messages`)
        .then((r) => (r.ok ? r.json() : []))
        .then((hist) => hist.length && setMessages([WELCOME, ...hist]))
        .catch(() => {});
    }
  }, [open]);

  const ensureConversation = async () => {
    if (convoRef.current) return convoRef.current;
    const r = await fetch(`${API}/api/public/ai/conversations`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_type: "public", source_channel: "website" }),
    });
    const data = await r.json();
    convoRef.current = data.id;
    sessionStorage.setItem(STORAGE_KEY, data.id);
    return data.id;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, sender_type: "user", message: text }]);
    const draftId = `ai-${Date.now()}`;
    setMessages((m) => [...m, { id: draftId, sender_type: "ai_agent", message: "", streaming: true }]);
    try {
      const cid = await ensureConversation();
      const res = await fetch(`${API}/api/public/ai/conversations/${cid}/messages/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const data = JSON.parse(ev.slice(6));
          if (data.delta) {
            full += data.delta;
            const snapshot = full;
            setMessages((m) => m.map((x) => (x.id === draftId ? { ...x, message: snapshot } : x)));
          } else if (data.done) {
            setMessages((m) => m.map((x) =>
              (x.id === draftId ? { ...x, message: data.message, streaming: false } : x)));
          }
        }
      }
    } catch {
      setMessages((m) => m.map((x) => (x.id === draftId
        ? { ...x, streaming: false, message: "Sorry, I couldn't connect just now — please try again or use the Enquiry page." }
        : x)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-40 right-4 sm:right-6 z-50 w-[min(92vw,380px)] rounded-xl border bg-card shadow-2xl flex flex-col overflow-hidden"
          style={{ height: "min(70vh, 520px)" }} data-testid="chat-widget-panel">
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-2.5">
            <Sparkles className="h-4 w-4" />
            <div className="flex-1">
              <p className="text-sm font-bold leading-none">Ask Cornerstone</p>
              <p className="text-[10px] opacity-75 mt-0.5">AI assistant · answers about courses, fees & enrolment</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" className="opacity-80 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="chat-widget-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.sender_type === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                  data-testid={`chat-msg-${m.sender_type}`}>
                  {m.message || (m.streaming ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t p-2.5 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…" className="h-9 text-sm" data-testid="chat-widget-input" />
            <Button size="sm" className="h-9 px-3" onClick={send} disabled={busy || !input.trim()}
              data-testid="chat-widget-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(!open)} data-testid="chat-widget-toggle"
        className="fixed bottom-20 right-4 sm:right-6 z-50 h-14 w-14 rounded-full bg-secondary text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Chat with us">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
}

